/**
 * The current tab's Station — connection, connectors, active transactions —
 * exposed as a Zustand store. The store mediates the protocol layer (adapter
 * + dispatcher + transport) on behalf of the UI.
 *
 * Session 6 scope: enough state to drive CP-01 (boot → authorize → start tx
 * → stop tx) end-to-end. Persistence wiring (saved stations, recovery) lands
 * in Session 7; charging profiles + offline queue in Session 8.
 */
import { create } from 'zustand';
import {
  ConnectionState,
  ConnectorStatus,
  Availability,
  type ConnectorId,
  type AuthStatus,
  type EvseId,
  type OcppVersion,
  type StationId,
  TransactionId,
  type VehicleProfileId,
} from '@/domain/types';
import { buildSampledValues } from '@/protocol/v16/actions/meterValues';

import type {
  EVSEKind,
  OfflineUploadPort,
  TransactionSnapshot,
  VehicleProfileSnapshot,
} from '@/domain';
import { AuthCache } from '@/domain/AuthCache';
import { DomainEventEmitter } from '@/domain/events';
import {
  computeDeltaSocPct,
  computeEffectiveWatts,
  clampSocPct,
} from '@/domain/VehicleProfile';
import {
  KEY_BATCH_OFFLINE_METER_VALUES,
  KEY_CP_MODEL,
  KEY_CP_VENDOR,
  OcppV16Adapter,
} from '@/protocol/v16/OcppV16Adapter';
import { KEY_V201_HEARTBEAT_INTERVAL } from '@/protocol/v201/variables';
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { createAdapter } from '@/protocol/createAdapter';
import type { OcppAdapter } from '@/protocol/OcppAdapter';
import {
  CallDispatcher,
  OcppWebSocketTransport,
  ReconnectStrategy,
  type WebSocketFactory,
} from '@/protocol';
import type { OcppFrame } from '@/protocol/OcppCall';
import { buildOcppWsUrl } from '@/lib/buildOcppWsUrl';
import { errorMessage } from '@/lib/errorMessage';
import { isV2x, subprotocolFor } from '@/lib/ocppProfile';
import { appendTxSample } from '@/lib/txTelemetry';
import {
  AUTH_CACHE_TTL_MS,
  CHARGING_STATES,
  configRepo,
  connectorStateRepo,
  runtimes,
  stationRepo,
  topologyRepo,
  transactionRepo,
  transactionSamplesRepo,
  txBufferKey,
  txSampleBuffers,
  vehicleRepo,
} from './station/runtime';
import {
  connectorTypeForEvse,
  emptyState,
  parseStopReason,
  readMeasurandSet,
  tryLocalAuthorize,
} from './station/helpers';
import { Topology, type TopologySnapshot } from '@/domain/Topology';
import { validateTopology } from '@/domain/topologyValidation';
import { StopReason } from '@/domain/types';
import { useConfigurationStore } from './useConfigurationStore';
import { useRegistryStore } from './useRegistryStore';
import { handleCsmsCall } from './csmsCallHandler';
import { csmsSession } from './csmsSessionState';
import { useCostStore } from './useCostStore';
import { useTabLockStore } from './useTabLockStore';
import { useEventLogStore } from './useEventLogStore';
import { useDiagnosticsStore } from './useDiagnosticsStore';
import { OCPP_MESSAGE_TYPE } from '@/protocol/OcppCall';
import { wrapCallClearingErrorOnSuccess } from './clearErrorOnSuccess';
import { canTransition } from '@/domain/connectorStatusTransitions';

export interface ConnectorState {
  readonly id: ConnectorId;
  readonly evseId: EvseId;
  readonly status: ConnectorStatus;
  readonly availability: Availability;
  readonly activeTransactionId: TransactionId | null;
  readonly activeIdTag: string | null;
  readonly requestedPowerW: number;
  readonly effectivePowerW: number;
  /** FEAT-011 — vehicle bound to this connector, if any. Persisted. */
  readonly assignedVehicleId: VehicleProfileId | null;
  /** FEAT-011 — running SoC (%) for DC sessions. Null for AC / unassigned. */
  readonly currentSocPct: number | null;
  /**
   * BUG-023 — running Energy.Active.Import.Register (Wh). Acts like a
   * real CP's cumulative meter: starts at 0 when a tx starts, increments
   * each SoC tick by `effectivePowerW × deltaSec / 3600`, and `meterStop`
   * on StopTransaction reads this field directly. Persisted via
   * connectorStateRepo so a refresh mid-tx resumes from the saved value
   * instead of rebuilding from a snapshot.
   */
  readonly accumulatedEnergyWh: number;
}

export interface EvseState {
  readonly id: EvseId;
  readonly name: string;
  readonly maxPowerW: number;
  readonly connectorIds: readonly ConnectorId[];
  /**
   * FEAT-011 — explicit AC/DC selection. Optional for backward compatibility
   * with topologies persisted before this field existed; `connectorTypeForEvse`
   * falls back to the name/power heuristic when absent.
   */
  readonly kind?: EVSEKind;
}

export interface AuthBanner {
  readonly idTag: string;
  readonly status: AuthStatus | 'Pending';
  readonly at: number;
}

export interface StationStoreState {
  stationId: StationId;
  cpid: string;
  wsUrl: string;
  /**
   * Cosmetic label for the station (RegistryPage, SettingsTab, browser
   * tab title). Mirrored from the persisted `stations.displayName`
   * column at init() so the Settings input can hydrate without
   * re-reading Dexie. `null` means "no display name set" — the UI
   * falls back to `${cpid}·${displayChecksum}`.
   */
  displayName: string | null;
  ocppVersion: OcppVersion;
  connectionState: ConnectionState;
  cpMaxPowerW: number;
  evses: readonly EvseState[];
  connectors: ReadonlyMap<ConnectorId, ConnectorState>;
  authBanner: AuthBanner | null;
  lastError: string | null;
}

export interface StationStoreActions {
  init(opts: {
    stationId: StationId;
    cpid: string;
    wsUrl: string;
    ocppVersion: OcppVersion;
    topology: { cpMaxPowerW: number; evses: readonly EvseState[] };
    webSocketFactory?: WebSocketFactory;
  }): void;
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  authorize(idTag: string): Promise<void>;
  startTransaction(connectorId: ConnectorId, idTag: string): Promise<void>;
  /**
   * Stop the active tx on `connectorId`. Optional `reason` ends up in
   * the StopTransaction CALL — used by BUG-022 to send
   * `EVDisconnected` when the connector status moves out of charging
   * while a tx is still open. Default `StopReason.Local` matches the
   * user-driven Stop button.
   */
  stopTransaction(connectorId: ConnectorId, reason?: StopReason): Promise<void>;
  /**
   * Build an OfflineUploadPort backed by the current OcppV16Adapter. Returns
   * null when no adapter is wired yet (Connect hasn't been clicked, or the
   * runtime was torn down). Callers must handle the null branch.
   */
  createUploadPort(): OfflineUploadPort | null;
  /**
   * The live OcppV201Adapter, or null when no station is connected or the
   * connected station is OCPP 1.6J. Lets the (v2.0.1-only) Plug & Charge store
   * drive PnC outbound calls without reaching into the runtime map.
   */
  activeV201Adapter(): OcppV201Adapter | null;
  /**
   * Fire a single generic CALL through the live CallDispatcher and return
   * the parsed payload. Used by the Replay tab to ship arbitrary captured
   * frames; production code should prefer the typed adapter methods.
   * Throws when no transport is wired or the CALL fails (CALLERROR /
   * timeout). The transactionId field (if any) is surfaced on the result.
   */
  sendReplayCall(
    action: string,
    payload: Record<string, unknown>,
  ): Promise<{ transactionId?: number }>;
  /** Update the station's friendly display name in Dexie. */
  setDisplayName(name: string | null): Promise<void>;
  /**
   * Session 17 — clear the in-memory `lastError` so the navbar error
   * banner closes. The next failure will set it again. Pure UI action;
   * no Dexie / WS side effects.
   */
  clearError(): void;
  /**
   * Session 13 — change the station's identity (cpid + wsUrl) while
   * Disconnected. Rejects when a transport is live (the lock is keyed
   * on (cpid, wsUrl) — a mid-flight rename would race the heartbeat).
   * Persists via the registry path so the (cpid+wsUrl) uniqueness check
   * fires, then mirrors the new values into the in-memory store so the
   * Settings + Navbar reflect them without a reload.
   */
  updateIdentity(input: {
    cpid: string;
    wsUrl: string;
  }): Promise<{ ok: true } | { ok: false; error: string }>;
  /**
   * Manually set a connector's status, firing StatusNotification on the
   * live adapter (fire-and-forget). No-op when the transition is illegal
   * per `connectorStatusTransitions` (UI also greys out illegal options)
   * or the connector is unknown. Always updates local state on success.
   */
  setConnectorStatus(connectorId: ConnectorId, status: ConnectorStatus): void;
  /**
   * Fire a single Heartbeat CALL on demand (NIT-008). Surfaces any
   * error via `lastError`; the UI displays the spinner while the
   * promise is pending. No-op when no live adapter (Disconnected).
   */
  heartbeat(): Promise<void>;
  /**
   * Session 19e — fire a synthetic v2.0.1 NotifyEvent so a tester can
   * verify CSMS reception of the device-event stream. No-op on v1.6
   * (NotifyEvent is v2.0.1-only) or while disconnected.
   */
  fireDiagnosticEvent(): Promise<void>;
  /**
   * Manually report the last GetDiagnostics / GetLog upload as Uploaded.
   * A browser-only sim can't reach an ftp:// (or CORS-blocked) target, so the
   * tester saves the bundle to disk, places it where the CSMS expects, then
   * uses this to drive the CSMS happy-path. Sends DiagnosticsStatusNotification
   * (1.6) or LogStatusNotification (2.0.1, echoing the GetLog requestId).
   */
  markDiagnosticsUploaded(): Promise<void>;
  /**
   * BUG-017 — emit one MeterValues CALL per active transaction with the
   * current Energy.Active.Import.Register (Wh), Power.Active.Import (W),
   * and (DC only) SoC. Caller is StationPage's useInterval(60_000) —
   * matches the default MeterValueSampleInterval the CP advertises.
   * Fire-and-forget per connector; one failing send doesn't block the
   * others. No-op when no live adapter or nothing is charging.
   * Synchronous: BUG-023 simplified this to read the live accumulator
   * directly instead of looking up tx.startedAt from Dexie.
   */
  emitMeterValuesForActiveTx(): void;
  /**
   * Persist a new topology and rebuild the live connector map. Surviving
   * connectors keep their in-tab status / active transaction; new
   * connectors come up Available; removed connectors are dropped.
   * Returns the list of validation issues; empty list = saved.
   */
  setTopology(snapshot: TopologySnapshot): Promise<readonly string[]>;
  /**
   * FEAT-011 — bind a vehicle profile to a connector. Persists the binding
   * so a refresh resumes the same assignment. For DC connectors the SoC
   * seeds to the vehicle's `defaultStartSocPct`; AC connectors keep
   * `currentSocPct = null` (SoC doesn't run on AC in this v1 model).
   */
  assignVehicle(
    connectorId: ConnectorId,
    vehicle: VehicleProfileSnapshot,
  ): Promise<void>;
  /** FEAT-011 — unbind any vehicle from `connectorId`. */
  unassignVehicle(connectorId: ConnectorId): Promise<void>;
  /**
   * FEAT-011 — advance SoC by `deltaSec` seconds against every active DC
   * transaction's effective power. Idempotent on the no-tx case. Persists
   * the new SoC value (best-effort) so a refresh resumes mid-charge.
   */
  applySocTick(deltaSec: number): void;
  /**
   * Soft reset: clear in-tab connector state + open transactions for this
   * station, but keep the station record + saved profiles + (cpid, wsUrl).
   * Disconnects first if currently connected.
   */
  softReset(): Promise<void>;
  /**
   * Full reset: cascade-delete the entire station (every per-station table).
   * Caller should redirect to the registry afterwards.
   */
  fullReset(): Promise<void>;
  /**
   * BUG-028 / Session 8f.15 — wipe the per-station AuthCache (OCPP 1.6J
   * §5.2 ClearCache). No-op when no runtime exists yet (e.g. called
   * before init()). LocalAuthList rows are intentionally untouched.
   */
  clearAuthCache(): void;
}

export type StationStore = StationStoreState & StationStoreActions;


export const useStationStore = create<StationStore>((set, get) => {
  const tag: object = {};

  function patchConnector(id: ConnectorId, patch: Partial<ConnectorState>): void {
    const current = get().connectors;
    const existing = current.get(id);
    if (!existing) return;
    const next = new Map(current);
    next.set(id, { ...existing, ...patch });
    set({ connectors: next });
  }

  return {
    ...emptyState(),

    init(opts): void {
      // FEAT-011 — one-shot migration: topologies persisted before the
      // explicit `kind` field have none. Resolve it once (via the legacy
      // name/power heuristic) and pin it as an explicit value so the editor
      // shows a concrete AC/DC selection the user can override. New topologies
      // already carry `kind`, so this is a no-op for them.
      const evses: readonly EvseState[] = opts.topology.evses.map((e) =>
        e.kind === undefined ? { ...e, kind: connectorTypeForEvse(e) } : e,
      );
      // Seed connector + evse state.
      const connectors = new Map<ConnectorId, ConnectorState>();
      for (const evse of evses) {
        for (const cid of evse.connectorIds) {
          connectors.set(cid, {
            id: cid,
            evseId: evse.id,
            status: ConnectorStatus.Available,
            availability: Availability.Operative,
            activeTransactionId: null,
            activeIdTag: null,
            requestedPowerW: 0,
            effectivePowerW: 0,
            assignedVehicleId: null,
            currentSocPct: null,
            accumulatedEnergyWh: 0,
          });
        }
      }
      set({
        ...emptyState(),
        stationId: opts.stationId,
        cpid: opts.cpid,
        wsUrl: opts.wsUrl,
        ocppVersion: opts.ocppVersion,
        cpMaxPowerW: opts.topology.cpMaxPowerW,
        evses,
        connectors,
      });
      // Session 13 — hydrate `displayName` from the persisted station
      // row so the Settings input reflects what's saved. Fire-and-forget;
      // a missing row (first boot) just leaves the field null.
      void stationRepo
        .get(opts.stationId)
        .then((row) => {
          if (row?.displayName !== undefined) {
            set({ displayName: row.displayName });
          }
        })
        .catch(() => undefined);
      // FEAT-006: rehydrate the Event Log ring buffer from Dexie so F5
      // doesn't lose the trail. Fire-and-forget — failures must not block
      // the boot path. The append path mirrors live frames into Dexie too,
      // so subsequent ticks stay in sync.
      void useEventLogStore.getState().hydrate(opts.stationId);
      // FEAT-011: hydrate vehicle binding + SoC from Dexie. Fire-and-forget.
      void hydrateConnectorState(opts.stationId);
      // Stash the factory until connect() actually creates a transport.
      const runtime = runtimes.get(tag);
      if (runtime) runtime.factory = opts.webSocketFactory;
      else
        runtimes.set(tag, {
          // Placeholder values — replaced on connect(). The transport is the
          // single source of truth for liveness.
          transport: undefined as unknown as OcppWebSocketTransport,
          dispatcher: undefined as unknown as CallDispatcher,
          adapter: undefined as unknown as OcppAdapter,
          factory: opts.webSocketFactory,
          reconnectStrategy: new ReconnectStrategy(),
          bootSucceeded: false,
          userClosed: false,
          // BUG-028 — created at init() so it's available before connect()
          // and survives all (re)connects until the next init() call.
          authCache: new AuthCache(opts.stationId, new DomainEventEmitter(), {
            maxTtlMs: AUTH_CACHE_TTL_MS,
          }),
        });
    },

    connect(): Promise<void> {
      const s = get();
      if (s.connectionState !== ConnectionState.Disconnected) return Promise.resolve();
      set({ connectionState: ConnectionState.Connecting, lastError: null });

      const factory = runtimes.get(tag)?.factory;
      // OCPP 1.6J convention: the WebSocket URL the CSMS expects is
      // `<wsUrl>/<cpid>` — the user-configured base + cpid as a path segment.
      // buildOcppWsUrl handles trailing slashes, URL-encoding the cpid, and
      // refuses to double-append when the user already pasted the cpid into
      // the base URL. See src/lib/buildOcppWsUrl.ts.
      let effectiveUrl: string;
      try {
        effectiveUrl = buildOcppWsUrl(s.wsUrl, s.cpid);
      } catch (err) {
        // Reset to Disconnected (not Error) so the user can fix the URL/CPID
        // and click Connect again without an intermediate Disconnect step.
        set({
          connectionState: ConnectionState.Disconnected,
          lastError: errorMessage(err),
        });
        return Promise.resolve();
      }
      // Two-step wiring: build the transport, then the dispatcher. The
      // transport's onFrame closure refers to the dispatcher via a holder
      // object so we don't need a `let dispatcher!:` forward declaration.
      const holder: {
        dispatcher: CallDispatcher | null;
        adapter: OcppAdapter | null;
      } = { dispatcher: null, adapter: null };
      const subprotocol = subprotocolFor(s.ocppVersion);
      const transport = new OcppWebSocketTransport({
        url: effectiveUrl,
        subprotocol,
        ...(factory !== undefined ? { factory } : {}),
        onFrame: (frame: OcppFrame) => holder.dispatcher?.feed(frame),
        onOpen: () => {
          // First action after WS open: BootNotification.
          void boot();
        },
        onClose: (code, reason) => {
          // Session 13 / CR mimo-rozsahu #9 — distinguish user-initiated
          // disconnect (don't retry) from transient drop after a
          // successful boot (retry with exponential backoff). The
          // `userClosed` flag is set by the `disconnect` action before
          // it calls `transport.close()`; `bootSucceeded` flips to
          // true on the boot Accepted branch.
          const runtime = runtimes.get(tag);
          const shouldRetry =
            !!runtime &&
            !runtime.userClosed &&
            runtime.bootSucceeded &&
            // Codes that suggest a transient drop. 1000 (Normal Closure)
            // and 1001 (Going Away) are deliberate; everything else is
            // suspect and worth a retry.
            code !== 1000 &&
            code !== 1001;
          set((s) => {
            // BUG-006 fix: when a connection attempt fails (e.g. host
            // unreachable, wrong port, TLS handshake error) browsers fire
            // `onerror` first with a bare DOM Event and then `onclose` with
            // code 1006. The Event carries no useful detail, so we'd
            // previously surface "[object Event]" via errorMessage(). If we
            // never reached Connected, replace whatever onerror wrote with
            // a friendly "Could not connect to <url>" line that includes
            // the close code/reason when present.
            const wasConnecting =
              s.connectionState === ConnectionState.Connecting;
            const next: Partial<StationStoreState> = {
              connectionState: shouldRetry
                ? ConnectionState.Reconnecting
                : ConnectionState.Disconnected,
            };
            if (wasConnecting) {
              const detail =
                reason && reason.length > 0
                  ? `: ${reason}`
                  : code !== undefined
                    ? ` (code ${code})`
                    : '';
              next.lastError = `Could not connect to ${effectiveUrl}${detail}`;
            }
            return next;
          });
          // BUG-003 fix: pending CALLs would otherwise sit on the 30s
          // dispatcher timeout when the WS closes silently — fail them
          // immediately so spinners (e.g. Authorize) reset to idle.
          holder.dispatcher?.failAll(new Error('connection closed'));
          if (shouldRetry && runtime) {
            const delay = runtime.reconnectStrategy.next();
            setTimeout(() => {
              // Re-check the flag in case the user clicked Disconnect
              // during the wait.
              const r = runtimes.get(tag);
              if (!r || r.userClosed) return;
              r.bootSucceeded = false;
              transport.connect();
              set({ connectionState: ConnectionState.Connecting });
            }, delay);
          }
        },
        onError: (err) => {
          set({ lastError: errorMessage(err) });
        },
      });
      const dispatcher = new CallDispatcher({
        send: (frame) => transport.send(frame),
        onIncomingCall: (frame) => {
          // Wired in Session 8f.4 — route CSMS-initiated CALLs through
          // adapter.parseIncoming and a free handler. The handler is
          // async + fire-and-forget; CSMS gets either CALLRESULT (happy)
          // or CALLERROR (validation / handler crash).
          const adapter = holder.adapter;
          const dispatcherInst = holder.dispatcher;
          if (!adapter || !dispatcherInst) return;
          void handleCsmsCall(
            frame,
            adapter,
            dispatcherInst,
            get().stationId,
          ).catch((err: unknown) => {
            set({ lastError: errorMessage(err) });
          });
        },
        // Every frame flowing through the dispatcher gets mirrored into the
        // UI EventLogStore so the Event Log tab can render the full timeline
        // with no extra plumbing per action.
        onFrameObserved: (frame, direction) => {
          const [type] = frame;
          if (type === OCPP_MESSAGE_TYPE.CALL) {
            useEventLogStore.getState().append({
              direction,
              messageType: 'CALL',
              messageId: frame[1],
              action: frame[2],
              payload: frame[3],
              at: Date.now(),
            });
            return;
          }
          if (type === OCPP_MESSAGE_TYPE.CALLRESULT) {
            useEventLogStore.getState().append({
              direction,
              messageType: 'CALLRESULT',
              messageId: frame[1],
              action: null,
              payload: frame[2],
              at: Date.now(),
            });
            return;
          }
          if (type === OCPP_MESSAGE_TYPE.CALLERROR) {
            useEventLogStore.getState().append({
              direction,
              messageType: 'CALLERROR',
              messageId: frame[1],
              action: null,
              payload: { errorCode: frame[2], errorDescription: frame[3], details: frame[4] },
              at: Date.now(),
            });
          }
        },
      });
      holder.dispatcher = dispatcher;
      // Assign adapter to holder right after construction so onIncomingCall
      // (which fires once the WS is up) can route through it.
      // Session 10d: createAdapter dispatches on station.ocppVersion so a
      // 2.0.1 station gets an OcppV201Adapter automatically. The
      // wrapCallClearingErrorOnSuccess (BUG-002) and topology getter (10b)
      // plumb through identically for both versions.
      const adapter = createAdapter({
        version: s.ocppVersion,
        sink: {
          call: wrapCallClearingErrorOnSuccess(
            (action, payload, timeoutMs) => dispatcher.call(action, payload, timeoutMs),
            {
              getLastError: () => get().lastError,
              clearLastError: () => set({ lastError: null }),
            },
          ),
        },
        topology: () => {
          const snap = get();
          if (snap.evses.length === 0) return null;
          try {
            return new Topology({ cpMaxPowerW: snap.cpMaxPowerW, evses: snap.evses });
          } catch {
            return null;
          }
        },
      });
      holder.adapter = adapter;
      // Preserve the reconnect strategy across (re)connect attempts so
      // exponential backoff actually progresses. Same for the AuthCache —
      // a transient WS drop must not invalidate cached idTag responses
      // (would re-issue Authorize on the post-reconnect grace period).
      const prevRuntime = runtimes.get(tag);
      const prevStrategy = prevRuntime?.reconnectStrategy;
      const prevAuthCache = prevRuntime?.authCache;
      runtimes.set(tag, {
        transport,
        dispatcher,
        adapter,
        factory,
        reconnectStrategy: prevStrategy ?? new ReconnectStrategy(),
        bootSucceeded: false,
        userClosed: false,
        authCache:
          prevAuthCache ??
          new AuthCache(get().stationId, new DomainEventEmitter(), {
            maxTtlMs: AUTH_CACHE_TTL_MS,
          }),
      });
      transport.connect();
      return Promise.resolve();

      async function boot(): Promise<void> {
        try {
          // Session 13 / CR mimo-rozsahu #14 — vendor + model are
          // configuration-driven so a tester can emulate different
          // hardware vendors. Falls back to the original hardcoded
          // defaults when the keys are missing (first-boot race
          // before useConfigurationStore hydrates).
          const cfg = useConfigurationStore.getState().entries;
          const chargePointVendor =
            cfg.find((e) => e.key === KEY_CP_VENDOR)?.value ?? 'VirtualCharger';
          const chargePointModel =
            cfg.find((e) => e.key === KEY_CP_MODEL)?.value ?? 'sim';
          const res = await adapter.bootNotification({
            chargePointVendor,
            chargePointModel,
          }).response;
          if (res.status === 'Accepted') {
            const now = Date.now();
            set({ connectionState: ConnectionState.Connected });
            // Session 13 / CR mimo-rozsahu #9 — boot Accepted means we
            // had a real round-trip; the next transient drop is worth
            // retrying. Reset the backoff window too so a fresh
            // reconnect cycle starts at the initialMs.
            const runtime = runtimes.get(tag);
            if (runtime) {
              runtime.bootSucceeded = true;
              runtime.reconnectStrategy.reset();
            }
            // Session 11 / CR 2026-05-27 §2.2 — honour
            // BootNotificationResponse.interval per OCPP 1.6J §6.10:
            // "After receiving Accepted, the CP SHALL send Heartbeat
            // every X seconds where X is the response interval."
            // useConfigurationStore is the source of truth for the
            // heartbeat timer (StationPage reads it reactively, so a
            // setValue() recreates the timer at the new cadence). Pick
            // the version-aware key — 1.6 is flat, 2.0.1 is namespaced.
            const heartbeatKey = isV2x(get().ocppVersion)
              ? KEY_V201_HEARTBEAT_INTERVAL
              : 'HeartbeatInterval';
            void useConfigurationStore
              .getState()
              .setValue(get().stationId, heartbeatKey, String(res.interval))
              .catch(() => undefined);
            // Persist the station record on first successful boot. Subsequent
            // boots refresh lastConnectedAt. Best-effort: a Dexie failure here
            // mustn't block the connection.
            //
            // CR 2026-05-27 #13 — `createdAt` MUST NOT clobber an
            // existing row. Mirror `setTopology`'s pattern: keep the
            // original createdAt when the row already exists, only set
            // `now` for the first boot.
            const snap = get();
            const existing = await stationRepo
              .get(snap.stationId)
              .catch(() => null);
            stationRepo
              .upsert({
                id: snap.stationId,
                cpid: snap.cpid,
                wsUrl: snap.wsUrl,
                ocppVersion: snap.ocppVersion,
                topology: { cpMaxPowerW: snap.cpMaxPowerW, evses: snap.evses },
                createdAt: existing?.createdAt ?? now,
                lastConnectedAt: now,
              })
              .catch((err: unknown) => {
                set({ lastError: errorMessage(err) });
              });
            // Send an initial StatusNotification(Available) for every connector.
            for (const c of get().connectors.values()) {
              void adapter.statusNotification({
                connectorId: c.id,
                status: ConnectorStatus.Available,
              }).response;
            }
          } else {
            // Session 11 / CR 2026-05-27 §2.2 — Rejected / Pending boot
            // is NOT a terminal error. Per OCPP 1.6J §6.10 the CP MUST
            // retry after `interval` seconds. Schedule the retry on a
            // ReconnectStrategy so backoff applies if the CSMS keeps
            // refusing. We clamp the response interval to the strategy's
            // window so a hostile CSMS returning `interval=0` doesn't
            // hot-loop.
            const retryAfterMs = Math.max(1_000, res.interval * 1_000);
            set({
              connectionState: ConnectionState.Connecting,
              lastError: `BootNotification ${res.status} — retrying in ${res.interval}s`,
            });
            setTimeout(() => {
              void boot();
            }, retryAfterMs);
          }
        } catch (err) {
          set({
            connectionState: ConnectionState.Error,
            lastError: errorMessage(err),
          });
        }
      }
    },

    async disconnect(): Promise<void> {
      const runtime = runtimes.get(tag);
      // Session 13 / CR mimo-rozsahu #9 — suppress the auto-reconnect
      // path. Set BEFORE close() so the close handler observes the
      // flag synchronously.
      if (runtime) runtime.userClosed = true;
      runtime?.dispatcher?.failAll(new Error('disconnect'));
      runtime?.transport?.close();
      // Disconnect = "I'm done with this station" → release the cross-tab
      // lock too so another tab can pick it up immediately rather than
      // waiting for the reaper. The UI flips to Disconnected ONLY after
      // both the WS close and the lock release have settled, so any test
      // (or user) that observes "Disconnected" can trust the cross-tab
      // contract is also clean.
      await useTabLockStore.getState().release();
      set({ connectionState: ConnectionState.Disconnected });
    },

    async authorize(idTag: string): Promise<void> {
      const runtime = runtimes.get(tag);
      if (!runtime?.adapter) return;
      set({ authBanner: { idTag, status: 'Pending', at: Date.now() } });
      try {
        // SPEC-001 local-first path (8f.4) — authorize from the Local Auth
        // List before the CSMS when the connection-aware gate is open
        // (LocalPreAuthorize online / LocalAuthorizeOffline offline). See
        // tryLocalAuthorize for the OCPP 1.6J §9.1 semantics.
        const online = get().connectionState === ConnectionState.Connected;
        const localHit = await tryLocalAuthorize(get().stationId, idTag, { online });
        if (localHit !== null) {
          set({
            authBanner: { idTag, status: localHit, at: Date.now() },
          });
          return;
        }
        // BUG-028 / Session 8f.15 — AuthCache short-circuit (OCPP 1.6J §9.2).
        // When `AuthorizationCacheEnabled` is 'true', a non-expired cached
        // Accepted entry skips the CSMS round-trip entirely. The cache is
        // wiped by the CSMS-initiated ClearCache handler or by toggling
        // the config key. Only Accepted entries are cached; Rejected /
        // Blocked / Expired must always round-trip so the CSMS can update
        // its decision.
        const cacheEnabled =
          useConfigurationStore
            .getState()
            .entries.find((e) => e.key === 'AuthorizationCacheEnabled')?.value === 'true';
        if (cacheEnabled) {
          const cached = runtime.authCache.get(idTag);
          if (cached !== null) {
            set({
              authBanner: { idTag, status: 'Accepted', at: Date.now() },
            });
            return;
          }
        }
        const res = await runtime.adapter.authorize(idTag).response;
        set({
          authBanner: { idTag, status: res.idTagInfo.status, at: Date.now() },
        });
        if (cacheEnabled && res.idTagInfo.status === 'Accepted') {
          const now = Date.now();
          runtime.authCache.add({
            idTag,
            acceptedAt: now,
            expiresAt: now + AUTH_CACHE_TTL_MS,
            ...(res.idTagInfo.parentIdTag !== undefined
              ? { parentIdTag: res.idTagInfo.parentIdTag }
              : {}),
          });
        }
        // Session 11 / CR 2026-05-27 §2.1 (StopTransactionOnInvalidId).
        // When the user re-authorises mid-session and the CSMS rejects
        // the idTag, OCPP 1.6J §9.4 says the CP MUST stop any active
        // transaction using that idTag with `reason=DeAuthorized`.
        // Gated by the config key (default 'true', matches v16 defaults
        // and the v201 AuthCacheCtrlr semantics).
        if (res.idTagInfo.status !== 'Accepted') {
          const stopOnInvalid = useConfigurationStore
            .getState()
            .entries.find((e) => e.key === 'StopTransactionOnInvalidId');
          if (stopOnInvalid === undefined || stopOnInvalid.value === 'true') {
            const active = [...get().connectors.values()].find(
              (c) => c.activeIdTag === idTag && c.activeTransactionId !== null,
            );
            if (active) {
              void get().stopTransaction(active.id, StopReason.DeAuthorized);
            }
          }
        }
      } catch (err) {
        set({ lastError: errorMessage(err), authBanner: null });
      }
    },

    async startTransaction(connectorId: ConnectorId, idTag: string): Promise<void> {
      const runtime = runtimes.get(tag);
      if (!runtime?.adapter) return;
      // Session 11 / CR 2026-05-27 §2.3 — block Start on an Inoperative
      // connector. The CSMS may have flipped availability via
      // ChangeAvailability; the connector contract is "no energy
      // delivery while Inoperative".
      const targetConnector = get().connectors.get(connectorId);
      if (targetConnector?.availability === Availability.Inoperative) {
        set({
          lastError: `Connector ${String(connectorId)} is Inoperative — cannot start a transaction.`,
        });
        return;
      }
      // BUG-013 — Start MUST NOT call Authorize. Authorize is a separate
      // top-of-page action (see AuthBanner). Conflating the two meant
      // every Start click double-authorized and aborted if the second
      // round-trip came back Invalid. The simulator now mirrors real-CP
      // behaviour: by the time Start fires, the idTag is presumed
      // already authorised (via the Authorize button or remotely via
      // RemoteStartTransaction). The CSMS is the final arbiter — it can
      // still reject the StartTransaction itself.

      // StatusNotification(Preparing).
      void runtime.adapter.statusNotification({
        connectorId,
        status: ConnectorStatus.Preparing,
      }).response;

      try {
        // SPEC-001 — honour local pre-authorization at start time. When the
        // idTag is approved by the Local Auth List under the active gate
        // (LocalPreAuthorize online / LocalAuthorizeOffline offline), the CP
        // trusts that local decision: a CSMS that doesn't recognise the
        // locally-whitelisted tag must NOT be able to veto the start. Without
        // this, a local whitelist is useless against a real CSMS — every
        // local-only idTag would be rejected by StartTransaction.conf.
        const online = get().connectionState === ConnectionState.Connected;
        const locallyAuthorized =
          (await tryLocalAuthorize(get().stationId, idTag, { online })) === 'Accepted';
        const startedAt = Date.now();
        const res = await runtime.adapter.startTransaction({
          connectorId,
          idTag,
          meterStart: 0,
          timestamp: new Date(startedAt).toISOString(),
        }).response;
        // Session 11 / CR 2026-05-27 §2.1 — honour idTagInfo.status per
        // OCPP 1.6J §6.30 and §4.7, UNLESS the start was locally
        // pre-authorized above. When not locally authorized and the CSMS
        // rejected the idTag (Invalid / Blocked / Expired / ConcurrentTx), we
        // MUST NOT patch the connector to Charging — that would be a real CP
        // delivering energy without authorisation. Send the connector back to
        // Available and surface the verdict in the auth banner. Note: the
        // v201 adapter synthesises this status from
        // TransactionEventResponse.idTokenInfo so the same gate works for both
        // wire formats.
        if (!locallyAuthorized && res.idTagInfo.status !== 'Accepted') {
          set({
            authBanner: {
              idTag,
              status: res.idTagInfo.status,
              at: Date.now(),
            },
          });
          void runtime.adapter.statusNotification({
            connectorId,
            status: ConnectorStatus.Available,
          }).response;
          return;
        }
        const txId = TransactionId.of(res.transactionId);
        // Persist the open transaction BEFORE the UI reflects the tx as
        // active. The UI flag is the testable signal that "this tx survives
        // a tab close", so we mustn't show Charging until Dexie has the row.
        try {
          await transactionRepo.upsert(get().stationId, {
            id: txId,
            connectorId,
            idTag,
            startedAt,
            startedMeterWh: 0,
          });
        } catch (err) {
          set({ lastError: errorMessage(err) });
        }
        // FEAT-011 — seed SoC on first charge for any assigned vehicle (AC or
        // DC). A connector with an already-running SoC preserves it (resume
        // mid-charge after restart).
        const conn = get().connectors.get(connectorId);
        const evse = get().evses.find((e) => e.id === conn?.evseId);
        const connectorType = connectorTypeForEvse(evse);
        let seededSoc: number | null = conn?.currentSocPct ?? null;
        let vehicle: VehicleProfileSnapshot | null = null;
        if (conn?.assignedVehicleId != null) {
          vehicle = await vehicleRepo.get(conn.assignedVehicleId).catch(() => null);
          if (vehicle && seededSoc === null) {
            seededSoc = vehicle.defaultStartSocPct;
            await connectorStateRepo
              .upsert({
                stationId: get().stationId,
                connectorId: Number(connectorId),
                assignedVehicleId: conn.assignedVehicleId,
                currentSocPct: seededSoc,
                updatedAt: Date.now(),
              })
              .catch(() => undefined);
          }
        }
        // FEAT-011 / "22 kW bug" — the delivered power follows the configured
        // limits, never a hardcoded constant. With a bound vehicle we cascade
        // min(vehicle, connector, evse, cp); without one we deliver the full
        // EVSE power (capped by the CP budget), so a DC EVSE configured at
        // 150 kW actually reports 150 kW instead of the old 22 kW default.
        const evseMaxW = evse?.maxPowerW ?? 0;
        const cpMaxW = get().cpMaxPowerW;
        const effectivePowerW = vehicle
          ? computeEffectiveWatts({
              vehicle,
              connectorType,
              connectorMaxW: evseMaxW,
              evseMaxW,
              cpMaxW,
              ...(seededSoc !== null ? { socPct: seededSoc } : {}),
            })
          : Math.max(0, Math.min(evseMaxW, cpMaxW));
        // BUG-023 — reset the energy register to 0 at the start of every
        // tx. The accumulator increments on each applySocTick and is the
        // single source of truth for both MV (Energy.Active.Import.
        // Register) and StopTransaction's meterStop.
        patchConnector(connectorId, {
          activeTransactionId: txId,
          activeIdTag: idTag,
          status: ConnectorStatus.Charging,
          requestedPowerW: effectivePowerW,
          effectivePowerW,
          currentSocPct: seededSoc,
          accumulatedEnergyWh: 0,
        });
        void runtime.adapter.statusNotification({
          connectorId,
          status: ConnectorStatus.Charging,
        }).response;
        // Tech-debt #3 — a transaction starting on a reserved connector
        // consumes the reservation: drop the record (and cancel its expiry
        // timer) so it can't later spuriously revert the now-charging
        // connector or emit ReservationStatusUpdate(Expired).
        const reservation = csmsSession.findReservationByConnector(
          get().stationId,
          Number(connectorId),
        );
        if (reservation) {
          csmsSession.removeReservation(get().stationId, reservation.reservationId);
        }
      } catch (err) {
        set({ lastError: errorMessage(err) });
      }
    },

    async stopTransaction(
      connectorId: ConnectorId,
      reason: StopReason = StopReason.Local,
    ): Promise<void> {
      const runtime = runtimes.get(tag);
      if (!runtime?.adapter) return;
      const connector = get().connectors.get(connectorId);
      if (!connector?.activeTransactionId) return;
      const txId = connector.activeTransactionId;
      const stoppedAt = Date.now();
      // BUG-023 — `meterStop` is the cumulative Energy.Active.Import.
      // Register reading at stop time. We maintain that register live
      // in `connector.accumulatedEnergyWh` (integrated each 1 Hz tick
      // by applySocTick) so it survives mid-tx power changes (taper,
      // suspend, vehicle unbind, ...) — identical semantics to a real
      // CP's energy register. Both the wire frame and the persisted
      // tx row reference this single value, so MV / Stop / Dexie stay
      // consistent by construction.
      const meterStopWh = Math.round(connector.accumulatedEnergyWh);
      try {
        // Session 11 / CR 2026-05-27 §3.3 — attach transactionData per
        // OCPP 1.6J §6.32. Always include the cumulative
        // Energy.Active.Import.Register reading (that's the floor of
        // what a real CP delivers); the CSL `StopTxnSampledData` only
        // controls ADDITIONAL measurands, which we don't yet model
        // (`Power.Active.Import` etc. are instantaneous and we don't
        // sample them at stop time). The single-measurand frame is
        // already enough to honour the spec contract — CSMS test
        // suites just check the field is present + parses.
        const transactionData = [
          {
            timestamp: new Date(stoppedAt).toISOString(),
            sampledValue: [
              {
                value: String(meterStopWh),
                measurand: 'Energy.Active.Import.Register',
                context: 'Transaction.End',
                unit: 'Wh',
              },
            ],
          },
        ];
        await runtime.adapter.stopTransaction({
          transactionId: txId,
          meterStop: meterStopWh,
          timestamp: new Date(stoppedAt).toISOString(),
          reason,
          transactionData,
        }).response;
        patchConnector(connectorId, {
          activeTransactionId: null,
          activeIdTag: null,
          status: ConnectorStatus.Finishing,
          requestedPowerW: 0,
          effectivePowerW: 0,
          // BUG-023 — reset the energy register so the next tx starts at 0.
          accumulatedEnergyWh: 0,
        });
        void runtime.adapter.statusNotification({
          connectorId,
          status: ConnectorStatus.Available,
        }).response;
        patchConnector(connectorId, { status: ConnectorStatus.Available });
        // Close the persisted row with the same meterStopWh sent on the wire.
        const existing = await transactionRepo
          .byConnector(get().stationId, connectorId)
          .catch(() => [] as readonly TransactionSnapshot[]);
        const open = existing.find((t) => t.id === txId);
        if (open) {
          transactionRepo
            .upsert(get().stationId, {
              ...open,
              stoppedAt,
              stoppedMeterWh: meterStopWh,
              stopReason: reason,
            })
            .catch((err: unknown) => {
              set({ lastError: errorMessage(err) });
            });
        }
        // TG-B — persist the captured progress series for this tx, then drop
        // the live buffer. Best-effort: a failed write just means no history
        // graph for this tx.
        const seriesKey = txBufferKey(get().stationId, Number(txId));
        const series = txSampleBuffers.get(seriesKey);
        if (series && series.length > 0) {
          void transactionSamplesRepo
            .upsert(get().stationId, Number(txId), series)
            .catch((err: unknown) => {
              set({ lastError: errorMessage(err) });
            });
        }
        txSampleBuffers.delete(seriesKey);
        // Session 11 / CR 2026-05-27 §2.3 — apply a deferred
        // ChangeAvailability the CSMS asked for while this tx was
        // active. Acts only when the deferred entry targets this
        // connector (or the whole station via connectorId=0).
        const deferred = csmsSession.getDeferredAvailability(get().stationId);
        if (
          deferred &&
          (deferred.connectorId === 0 || deferred.connectorId === connectorId)
        ) {
          csmsSession.clearDeferredAvailability(get().stationId);
          const targetAvail =
            deferred.type === 'Operative'
              ? Availability.Operative
              : Availability.Inoperative;
          const wireStatus =
            targetAvail === Availability.Operative
              ? ConnectorStatus.Available
              : ConnectorStatus.Unavailable;
          const targets =
            deferred.connectorId === 0
              ? [...get().connectors.values()]
              : [get().connectors.get(connectorId)].filter(
                  (c): c is ConnectorState => c !== undefined,
                );
          for (const c of targets) {
            patchConnector(c.id, { availability: targetAvail });
            void runtime.adapter.statusNotification({
              connectorId: c.id,
              status: wireStatus,
            }).response;
          }
        }
      } catch (err) {
        set({ lastError: errorMessage(err) });
      }
    },

    async sendReplayCall(
      action: string,
      payload: Record<string, unknown>,
    ): Promise<{ transactionId?: number }> {
      const runtime = runtimes.get(tag);
      if (!runtime?.dispatcher) {
        throw new Error('sendReplayCall: no live dispatcher — connect first');
      }
      const { response } = runtime.dispatcher.call(action, payload);
      const result = await response;
      const txIdRaw = result['transactionId'];
      return typeof txIdRaw === 'number' ? { transactionId: txIdRaw } : {};
    },

    activeV201Adapter(): OcppV201Adapter | null {
      const adapter = runtimes.get(tag)?.adapter;
      return adapter instanceof OcppV201Adapter ? adapter : null;
    },

    createUploadPort(): OfflineUploadPort | null {
      const runtime = runtimes.get(tag);
      if (!runtime?.adapter) return null;
      const adapter = runtime.adapter;
      const stationId = get().stationId;
      return {
        async upload(_stationId, frames): Promise<number> {
          const startedAt = new Date(frames.start.startedAtMs).toISOString();
          const startRes = await adapter.startTransaction({
            connectorId: frames.start.connectorId,
            idTag: frames.start.idTag,
            meterStart: frames.start.meterStartWh,
            timestamp: startedAt,
          }).response;
          const txId = TransactionId.of(startRes.transactionId);
          if (frames.meterValues.length > 0) {
            // FEAT-005 — default sends one CALL per sample (matches evse2);
            // the Configuration toggle Evse3.BatchOfflineMeterValues=true
            // restores the single-batched-CALL behaviour for users who
            // want the optimised path.
            const batchSetting = await configRepo
              .get(stationId, KEY_BATCH_OFFLINE_METER_VALUES)
              .catch(() => null);
            const batch = batchSetting?.value === 'true';
            const sampledFor = (
              m: (typeof frames.meterValues)[number],
            ): {
              timestamp: string;
              sampledValue: { value: string; measurand: string; unit: string }[];
            } => ({
              timestamp: new Date(m.atMs).toISOString(),
              sampledValue: [
                {
                  value: String(m.energyWh),
                  measurand: 'Energy.Active.Import.Register',
                  unit: 'Wh',
                },
                { value: String(m.powerW), measurand: 'Power.Active.Import', unit: 'W' },
                ...(m.socPct !== undefined
                  ? [{ value: String(m.socPct), measurand: 'SoC', unit: 'Percent' }]
                  : []),
              ],
            });
            if (batch) {
              await adapter.meterValues({
                connectorId: frames.start.connectorId,
                transactionId: txId,
                meterValue: frames.meterValues.map(sampledFor),
              }).response;
            } else {
              for (const m of frames.meterValues) {
                await adapter.meterValues({
                  connectorId: frames.start.connectorId,
                  transactionId: txId,
                  meterValue: [sampledFor(m)],
                }).response;
              }
            }
          }
          await adapter.stopTransaction({
            transactionId: txId,
            meterStop: frames.stop.meterStopWh,
            timestamp: new Date(frames.stop.stoppedAtMs).toISOString(),
            reason: parseStopReason(frames.stop.reason),
          }).response;
          return startRes.transactionId;
        },
      };
    },

    setConnectorStatus(connectorId: ConnectorId, status: ConnectorStatus): void {
      const s = get();
      const connector = s.connectors.get(connectorId);
      if (!connector) return;
      // Symmetric gating with the UI dropdown — refuse illegal moves even if
      // the dropdown was bypassed (e.g. via the store directly from tests).
      if (!canTransition(connector.status, status)) return;
      const previousStatus = connector.status;
      const runtime = runtimes.get(tag);
      // Fire StatusNotification if we have a live adapter; otherwise the
      // local-state update still happens so the dropdown stays usable
      // before Connect.
      if (runtime?.adapter) {
        void runtime.adapter.statusNotification({ connectorId, status }).response;
      }
      patchConnector(connectorId, { status });

      // BUG-022 — auto-stop the active tx when the connector moves out of
      // a "charging" state while a tx is still open. The
      // StopTransactionOnEVSideDisconnect config key gates this (default
      // "true"). Per OCPP 1.6J the StopTransaction reason is then
      // EVDisconnected (we don't model the SuspendedEV / SuspendedEVSE
      // distinction here — any move OUT of the charging set counts).
      if (
        connector.activeTransactionId !== null &&
        CHARGING_STATES.has(previousStatus) &&
        !CHARGING_STATES.has(status)
      ) {
        const cfg = useConfigurationStore
          .getState()
          .entries.find((e) => e.key === 'StopTransactionOnEVSideDisconnect');
        // Default true when the key is missing (matches the seeded default).
        const autoStop = cfg === undefined ? true : cfg.value !== 'false';
        if (autoStop) {
          void get().stopTransaction(connectorId, StopReason.EVDisconnected);
        }
      }
    },

    async heartbeat(): Promise<void> {
      const runtime = runtimes.get(tag);
      if (!runtime?.adapter) return;
      try {
        await runtime.adapter.heartbeat().response;
      } catch (err) {
        set({ lastError: errorMessage(err) });
      }
    },

    async fireDiagnosticEvent(): Promise<void> {
      // Session 19e — manual NotifyEvent for QA. v2.0.1-only; a synthetic
      // "OCPPCommCtrlr.HeartbeatInterval changed" alerting event.
      const runtime = runtimes.get(tag);
      const adapter = runtime?.adapter;
      if (!(adapter instanceof OcppV201Adapter)) {
        set({ lastError: 'NotifyEvent is only available on OCPP 2.0.1 stations.' });
        return;
      }
      const now = new Date(Date.now()).toISOString();
      try {
        await adapter.notifyEvent({
          generatedAt: now,
          seqNo: 0,
          tbc: false,
          eventData: [
            {
              eventId: 1,
              timestamp: now,
              trigger: 'Alerting',
              actualValue: 'diagnostic-test',
              eventNotificationType: 'CustomMonitor',
              component: 'OCPPCommCtrlr',
              variable: 'HeartbeatInterval',
              techInfo: 'Manual diagnostic event from VirtualCharger',
            },
          ],
        }).response;
      } catch (err) {
        set({ lastError: errorMessage(err) });
      }
    },

    async markDiagnosticsUploaded(): Promise<void> {
      const runtime = runtimes.get(tag);
      const adapter = runtime?.adapter;
      if (!adapter) {
        set({ lastError: 'Connect first to report the upload.' });
        return;
      }
      const diag = useDiagnosticsStore.getState();
      try {
        if (diag.kind === 'log' && adapter instanceof OcppV201Adapter) {
          await adapter.logStatusNotification({
            status: 'Uploaded',
            ...(diag.requestId !== null ? { requestId: diag.requestId } : {}),
          }).response;
        } else if (adapter instanceof OcppV16Adapter) {
          await adapter.diagnosticsStatusNotification('Uploaded').response;
        } else {
          set({ lastError: 'No diagnostics upload to report for this station.' });
          return;
        }
        useDiagnosticsStore.getState().setUploaded();
      } catch (err) {
        set({ lastError: errorMessage(err) });
      }
    },

    emitMeterValuesForActiveTx(): void {
      // BUG-017 — periodic MeterValues during an active transaction.
      // BUG-020 — measurand list is read reactively from the
      // MeterValuesSampledData config CSL. Supported measurands:
      //   Energy.Active.Import.Register, Power.Active.Import, SoC,
      //   Voltage, Current.Import. Anything else in the CSL is ignored
      //   (this charger doesn't model it). Empty / missing CSL falls
      //   back to the documented default.
      // BUG-023 — Energy.Active.Import.Register now reads the live
      // per-connector accumulator (`connector.accumulatedEnergyWh`).
      // No need for tx.startedAt + recompute — the field is integrated
      // on each 1 Hz applySocTick and is the single source of truth
      // shared with StopTransaction.meterStop.
      const runtime = runtimes.get(tag);
      if (!runtime?.adapter) return;
      const s = get();
      const connectors = [...s.connectors.values()].filter(
        (c) => c.activeTransactionId !== null,
      );
      if (connectors.length === 0) return;
      const measurandSet = readMeasurandSet();
      const nowMs = Date.now();
      const timestamp = new Date(nowMs).toISOString();
      const adapter = runtime.adapter;
      for (const c of connectors) {
        const txId = c.activeTransactionId;
        if (txId === null) continue;
        const evse = s.evses.find((e) => e.id === c.evseId);
        const connectorType = connectorTypeForEvse(evse);
        const sampledValue = buildSampledValues({
          measurandSet,
          connectorType,
          effectivePowerW: c.effectivePowerW,
          accumulatedEnergyWh: c.accumulatedEnergyWh,
          currentSocPct: c.currentSocPct,
        });
        if (sampledValue.length === 0) continue; // CSL filtered everything out
        // Fire-and-forget. A single failing send must not block the others
        // or the next interval tick.
        adapter
          .meterValues({
            connectorId: c.id,
            transactionId: txId,
            meterValue: [{ timestamp, sampledValue }],
          })
          .response.catch((err: unknown) => {
            set({ lastError: errorMessage(err) });
          });
      }
    },

    async setTopology(snapshot: TopologySnapshot): Promise<readonly string[]> {
      const issues = validateTopology(snapshot);
      if (issues.length > 0) return issues;
      const s = get();
      // Persist first; if Dexie chokes, surface the error and abandon the
      // local rebuild so live state stays consistent with persistence.
      try {
        // Also write a station row so the URL boot intent can rehydrate
        // this topology on reload — without this, an ephemeral station
        // would never reach the `stations` table until first successful
        // boot, and the topology change would be lost.
        const existing = await stationRepo.get(s.stationId);
        await stationRepo.upsert({
          id: s.stationId,
          cpid: s.cpid,
          wsUrl: s.wsUrl,
          ...(existing?.displayName !== undefined
            ? { displayName: existing.displayName }
            : {}),
          ocppVersion: s.ocppVersion,
          topology: snapshot,
          createdAt: existing?.createdAt ?? Date.now(),
          ...(existing?.lastConnectedAt !== undefined
            ? { lastConnectedAt: existing.lastConnectedAt }
            : {}),
        });
        await topologyRepo.upsert(s.stationId, snapshot);
      } catch (err) {
        set({ lastError: errorMessage(err) });
        return [errorMessage(err)];
      }
      // Rebuild the connector map preserving in-flight status / active tx
      // for surviving connectors; new connectors come up Available.
      const next = new Map<ConnectorId, ConnectorState>();
      for (const evse of snapshot.evses) {
        for (const cid of evse.connectorIds) {
          const existing = s.connectors.get(cid);
          if (existing) {
            next.set(cid, { ...existing, evseId: evse.id });
          } else {
            next.set(cid, {
              id: cid,
              evseId: evse.id,
              status: ConnectorStatus.Available,
              availability: Availability.Operative,
              activeTransactionId: null,
              activeIdTag: null,
              requestedPowerW: 0,
              effectivePowerW: 0,
              assignedVehicleId: null,
              currentSocPct: null,
              accumulatedEnergyWh: 0,
            });
          }
        }
      }
      set({
        cpMaxPowerW: snapshot.cpMaxPowerW,
        evses: snapshot.evses,
        connectors: next,
      });
      // Session 18 — sync the derived `NumberOfConnectors` row so the
      // CSMS-side GetVariables / GetConfiguration reply (and the
      // Settings table render) reflects the new count immediately.
      // Fire-and-forget: if Dexie misbehaves, the row stays stale and
      // surfaces on next refresh; we don't block the topology write
      // on a config-table update.
      void useConfigurationStore
        .getState()
        .applyDerivedFromTopology(s.stationId, s.ocppVersion, snapshot.evses)
        .catch(() => undefined);
      return [];
    },

    async updateIdentity(input: {
      cpid: string;
      wsUrl: string;
    }): Promise<{ ok: true } | { ok: false; error: string }> {
      if (get().connectionState !== ConnectionState.Disconnected) {
        return {
          ok: false,
          error: 'Disconnect first — identity (cpid / wsUrl) cannot change while the WebSocket is open.',
        };
      }
      const result = await useRegistryStore.getState().update(get().stationId, {
        cpid: input.cpid,
        wsUrl: input.wsUrl,
      });
      if (!result.ok) {
        return { ok: false, error: result.error ?? 'Update failed.' };
      }
      set({ cpid: input.cpid, wsUrl: input.wsUrl });
      return { ok: true };
    },

    clearError(): void {
      set({ lastError: null });
    },

    async setDisplayName(name: string | null): Promise<void> {
      const snap = get();
      const existing = await stationRepo.get(snap.stationId);
      if (!existing) return;
      const next = name === null || name.length === 0 ? undefined : name;
      await stationRepo.upsert({
        ...existing,
        ...(next !== undefined ? { displayName: next } : {}),
      });
      // Session 13 — mirror into in-memory state so the Settings input,
      // Navbar, and any future selector observe the new value without a
      // Dexie re-read. `null` after a clear (empty input → next ===
      // undefined → row no longer carries the field).
      set({ displayName: next ?? null });
    },

    async assignVehicle(
      connectorId: ConnectorId,
      vehicle: VehicleProfileSnapshot,
    ): Promise<void> {
      const s = get();
      const conn = s.connectors.get(connectorId);
      if (!conn) return;
      // FEAT-011 — seed currentSocPct to the vehicle's default start for any
      // connector type (AC or DC), so the UI shows the running SoC immediately,
      // even before Start. SoC tracking is gated on the bound vehicle, not the
      // connector type.
      const nextSoc = vehicle.defaultStartSocPct;
      try {
        await connectorStateRepo.upsert({
          stationId: s.stationId,
          connectorId: Number(connectorId),
          assignedVehicleId: vehicle.id,
          ...(nextSoc !== null ? { currentSocPct: nextSoc } : {}),
          updatedAt: Date.now(),
        });
      } catch (err) {
        set({ lastError: errorMessage(err) });
        return;
      }
      patchConnector(connectorId, {
        assignedVehicleId: vehicle.id,
        currentSocPct: nextSoc,
      });
    },

    async unassignVehicle(connectorId: ConnectorId): Promise<void> {
      const s = get();
      const conn = s.connectors.get(connectorId);
      if (!conn) return;
      try {
        await connectorStateRepo.delete(s.stationId, Number(connectorId));
      } catch (err) {
        set({ lastError: errorMessage(err) });
        return;
      }
      patchConnector(connectorId, {
        assignedVehicleId: null,
        currentSocPct: null,
      });
    },

    applySocTick(deltaSec: number): void {
      if (!(deltaSec > 0)) return;
      const s = get();
      if (s.connectors.size === 0) return;
      const now = Date.now();
      // Session 13 / CR mimo-rozsahu #11 — batch every per-connector
      // energy patch into a SINGLE setState at end-of-tick. The
      // previous loop called `patchConnector` once per connector,
      // each call producing a new Map + setState — which competed
      // with the async `advanceSocForConnector` writes for the same
      // `connectors` slot. A single batched setState closes that
      // window for the synchronous energy register. The async SoC
      // patch still happens fan-out (it has to await Dexie); it
      // merges with `liveConn` so the accumulator increments survive.
      let nextConnectors: Map<ConnectorId, ConnectorState> | null = null;
      const ensureMap = (): Map<ConnectorId, ConnectorState> => {
        if (nextConnectors === null) nextConnectors = new Map(s.connectors);
        return nextConnectors;
      };
      for (const [cid, conn] of s.connectors) {
        if (conn.activeTransactionId === null) continue;
        // BUG-023 — integrate the cumulative energy register on EVERY
        // active connector (AC or DC, vehicle bound or not). This is
        // the single source of truth that `meterStop` and
        // Energy.Active.Import.Register on MV both read from.
        const deltaWh = (conn.effectivePowerW * deltaSec) / 3600;
        if (deltaWh > 0) {
          const map = ensureMap();
          const live = map.get(cid) ?? conn;
          map.set(cid, {
            ...live,
            accumulatedEnergyWh: live.accumulatedEnergyWh + deltaWh,
          });
        }
        // TG-B — capture a progress sample (power/SoC/energy) for this tx on
        // every active connector; persisted to transactionSamples on stop.
        const txKey = txBufferKey(s.stationId, Number(conn.activeTransactionId));
        txSampleBuffers.set(
          txKey,
          appendTxSample(txSampleBuffers.get(txKey) ?? [], {
            t: now,
            powerW: conn.effectivePowerW,
            socPct: conn.currentSocPct,
            energyWh: conn.accumulatedEnergyWh + Math.max(0, deltaWh),
          }),
        );
        // FEAT-011 — SoC simulation runs for any bound vehicle (AC or DC);
        // the connector type only selects which power cap / curve applies
        // inside advanceSocForConnector.
        if (conn.assignedVehicleId === null) continue;
        // Async fan-out: read the vehicle, compute deltaSoc, patch state +
        // persist the new SoC. The patch is intentionally outside the
        // synchronous loop so the UI tick doesn't stall on Dexie reads.
        // The advance reads the LIVE connector at write time so its
        // `accumulatedEnergyWh` survives our batched update above.
        void advanceSocForConnector(cid, conn, deltaSec);
      }
      if (nextConnectors !== null) {
        set({ connectors: nextConnectors });
      }
    },

    async softReset(): Promise<void> {
      // Disconnect first (releases lock, closes WS) without nuking the
      // saved record.
      const s = get();
      if (s.connectionState !== ConnectionState.Disconnected) {
        await get().disconnect();
      }
      // Clear open transactions for this station — same store + Dexie.
      const openTxs = await transactionRepo.findOpen(s.stationId);
      for (const tx of openTxs) {
        await transactionRepo.delete(s.stationId, tx.id);
      }
      // Wipe the in-tab event log + Dexie eventLog rows + reset connector state.
      await useEventLogStore.getState().clear(s.stationId);
      // Session 27 (review) — drop transient CSMS-side state (reservations,
      // monitors, network profiles, certs, deferred availability, costs) so
      // a reset connector isn't left contradicted by stale records.
      csmsSession.clearStation(s.stationId);
      txSampleBuffers.clear();
      useCostStore.getState().clear();
      const cleared = new Map<ConnectorId, ConnectorState>();
      for (const [id, c] of s.connectors) {
        cleared.set(id, {
          ...c,
          status: ConnectorStatus.Available,
          availability: Availability.Operative,
          activeTransactionId: null,
          activeIdTag: null,
          requestedPowerW: 0,
          effectivePowerW: 0,
          // Soft reset keeps the vehicle binding (testers expect a refresh /
          // soft reset to leave the assigned car untouched). SoC resets to
          // null since no transaction is running. BUG-023 — also zero the
          // energy register so the next tx starts at 0.
          currentSocPct: null,
          accumulatedEnergyWh: 0,
        });
      }
      set({ connectors: cleared, authBanner: null, lastError: null });
    },

    async fullReset(): Promise<void> {
      const s = get();
      if (s.connectionState !== ConnectionState.Disconnected) {
        await get().disconnect();
      }
      // Cascade delete via StationRepositoryDexie.delete — wipes every
      // per-station table in one rw transaction.
      await stationRepo.delete(s.stationId);
      await useEventLogStore.getState().clear(s.stationId);
      // Session 27 (review) — also drop the in-memory CSMS-side state so a
      // deleted station leaves nothing behind (the maps are keyed by a
      // fresh UUID per station, so otherwise they grow unbounded).
      csmsSession.clearStation(s.stationId);
      txSampleBuffers.clear();
      useCostStore.getState().clear();
      set({
        ...emptyState(),
      });
    },

    clearAuthCache(): void {
      // BUG-028 / Session 8f.15 — implementation of OCPP 1.6J §5.2
      // ClearCache. No-op when the runtime hasn't been built yet (test
      // setups, or a CSMS frame racing init()). LocalAuthList Dexie rows
      // are intentionally untouched — those belong to SendLocalList per
      // OCPP §5.16.
      runtimes.get(tag)?.authCache.clear();
    },
  };
});


/**
 * FEAT-011 — Pull the persisted `connectorState` rows for this station from
 * Dexie and merge them into the in-memory connectors map. Best-effort; a
 * Dexie miss leaves connectors in their default seeded state.
 */
async function hydrateConnectorState(stationId: StationId): Promise<void> {
  let rows: readonly { connectorId: number; assignedVehicleId?: VehicleProfileId; currentSocPct?: number }[];
  try {
    rows = await connectorStateRepo.byStation(stationId);
  } catch {
    return;
  }
  if (rows.length === 0) return;
  const s = useStationStore.getState();
  // Guard against a station swap during the await — only apply hydration
  // if the store still points at the station we hydrated for.
  if (s.stationId !== stationId) return;
  const next = new Map(s.connectors);
  let mutated = false;
  for (const row of rows) {
    // Match by ConnectorId numeric equality; the brand is structural and
    // safe to compare across stations within the same Map.
    const cid = [...next.keys()].find((k) => Number(k) === row.connectorId);
    if (cid === undefined) continue;
    const existing = next.get(cid);
    if (!existing) continue;
    next.set(cid, {
      ...existing,
      assignedVehicleId: row.assignedVehicleId ?? null,
      currentSocPct: row.currentSocPct ?? null,
    });
    mutated = true;
  }
  if (mutated) useStationStore.setState({ connectors: next });
}

/**
 * FEAT-011 — Advance the SoC for a single connector by `deltaSec` seconds.
 * Reads the bound vehicle from Dexie, computes effectiveWatts against the
 * station's caps, applies `computeDeltaSocPct`, clamps to [0, 100], and
 * persists the new value. Idempotent on the missing-vehicle path.
 */
async function advanceSocForConnector(
  cid: ConnectorId,
  conn: ConnectorState,
  deltaSec: number,
): Promise<void> {
  if (conn.assignedVehicleId === null) return;
  const vehicle = await vehicleRepo.get(conn.assignedVehicleId).catch(() => null);
  if (!vehicle) return;
  const s = useStationStore.getState();
  const evse = s.evses.find((e) => e.id === conn.evseId);
  const connectorType = connectorTypeForEvse(evse);
  const evseMaxW = evse?.maxPowerW ?? 0;
  const cpMaxW = s.cpMaxPowerW;
  const currentSoc = conn.currentSocPct ?? vehicle.defaultStartSocPct;
  const effectiveWatts = computeEffectiveWatts({
    vehicle,
    // FEAT-011 — honour the explicit connector type: AC uses the flat
    // maxAcPowerKw, DC uses maxDcPowerKw (and the curve when present).
    connectorType,
    // No per-connector cap field in v1 — fall back to the EVSE cap.
    connectorMaxW: evseMaxW,
    evseMaxW,
    cpMaxW,
    // FEAT-010 (curve) — taper DC power by SoC when the vehicle has a curve.
    socPct: currentSoc,
  });
  const delta = computeDeltaSocPct({
    effectiveWatts,
    batteryKwh: vehicle.batteryKwh,
    deltaSec,
  });
  if (delta <= 0) return;
  const nextSoc = clampSocPct(currentSoc, delta);
  // Patch only if changed and the connector hasn't been swapped under us.
  const live = useStationStore.getState();
  if (live.stationId !== s.stationId) return;
  const liveConn = live.connectors.get(cid);
  if (!liveConn || liveConn.activeTransactionId !== conn.activeTransactionId) return;
  const map = new Map(live.connectors);
  map.set(cid, { ...liveConn, currentSocPct: nextSoc, effectivePowerW: effectiveWatts });
  useStationStore.setState({ connectors: map });
  connectorStateRepo
    .upsert({
      stationId: live.stationId,
      connectorId: Number(cid),
      assignedVehicleId: conn.assignedVehicleId,
      currentSocPct: nextSoc,
      updatedAt: Date.now(),
    })
    .catch(() => undefined);
}

/** Module-level convenience that delegates to the current store instance. */
export function sendReplayCall(
  action: string,
  payload: Record<string, unknown>,
): Promise<{ transactionId?: number }> {
  return useStationStore.getState().sendReplayCall(action, payload);
}

// errorMessage moved to @/lib/errorMessage so it can recognise DOM Event
// objects (WebSocket.onerror passes a bare Event whose default stringification
// is "[object Event]"). See src/lib/errorMessage.ts.


/**
 * BUG-014 — parse an inbound SetChargingProfile.req payload into a
 * persistable ChargingProfileSnapshot. Returns null when any field is
 * missing or has the wrong shape — caller should reply Rejected.
 *
 * Caller passes `connectorId` separately because the OCPP wire shape
 * carries it on the outer payload, not inside csChargingProfiles.
 */
// Session 12 / CR §4.1 — `readMeasurandSet`, `buildSampledValues`,
// SIM_* constants and the `BuildSampledValuesArgs` / `SampledValue`
// types now live in `protocol/v16/actions/meterValues.ts`. They are
// imported from the v16 actions barrel where the store still needs
// them at the MV emit call site.

// Session 12 / CR §4.1 — `parseSetChargingProfilePayload`,
// `parseChargingSchedule` now live in `protocol/v16/actions/setChargingProfile.ts`.
// `parseDateTime` is the generic helper in `lib/parseDateTime.ts`.

